import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import '../styles/pages/Home.css';
import '../styles/animations.css';
import { subscribeNewsletter } from '../contexts/db.Newsletter';
import { getReviews } from '../contexts/db.Reviews';
import Cloud1 from '../assets/images/cloud-1.png';
import Cloud2 from '../assets/images/cloud-2.png';

// Animation timing constants
const ANIMATION_DELAYS = {
  SUPPORTING_TEXT: 300,    // Start showing supporting text after 300ms
  FREE_TRIAL: 2500,        // Show free trial text after 2.5s
  STORE_BUTTONS: 4500      // Show store buttons after 4.5s
};


function Home({ language }) {
  const t = translations[language];
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [showSupportingText, setShowSupportingText] = useState(false);
  const [showFreeTrial, setShowFreeTrial] = useState(false);
  const [showStoreButtons, setShowStoreButtons] = useState(false);
  const [supportingText, setSupportingText] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const sectionsRef = useRef([]);
  
  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterRole, setNewsletterRole] = useState('parent');
  const [newsletterPrivacy, setNewsletterPrivacy] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState({ type: '', text: '' });
  
  // Gallery modal state
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  // Reviews state
  const [reviews, setReviews] = useState([]);

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('userData');
    
    if (token && user) {
      setIsAuthenticated(true);
      setUserData(JSON.parse(user));
    }
  }, []);

  // Fetch reviews on mount
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await getReviews();
        const list = response?.reviews || response || [];
        setReviews(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading reviews:', err);
      }
    };
    fetchReviews();
  }, []);


  const galleryImages = [
    require('../assets/gallery/1.jpeg'),
    require('../assets/gallery/2.jpeg'),
    require('../assets/gallery/3.jpeg'),
    require('../assets/gallery/4.jpeg'),
  ];

  // Typewriter effect for supporting text
  useEffect(() => {
    const text = t.heroSupportingText || ''; // Ensure text is never undefined
    let currentIndex = 0;
    let typeWriterTimer;
    
    const typeWriter = () => {
      if (currentIndex < text.length) {
        setSupportingText(text.substring(0, currentIndex + 1));
        currentIndex++;
        typeWriterTimer = setTimeout(typeWriter, 20); // Typing speed
      }
    };
    
    // Start the animation sequence
    const timer1 = setTimeout(() => {
      setShowSupportingText(true);
      if (text) {
        typeWriter();
      }
    }, ANIMATION_DELAYS.SUPPORTING_TEXT);
    
    const timer2 = setTimeout(() => {
      setShowFreeTrial(true);
    }, ANIMATION_DELAYS.FREE_TRIAL);
    
    const timer3 = setTimeout(() => {
      setShowStoreButtons(true);
    }, ANIMATION_DELAYS.STORE_BUTTONS);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(typeWriterTimer);
    };
  }, [t.heroSupportingText]);

  useEffect(() => {
    // Initialize sectionsRef array with enough elements
    sectionsRef.current = new Array(6).fill(null);

    // Ensure video plays
    // const playVideo = async () => {
    //   try {
    //     if (videoRef.current) {
    //       await videoRef.current.play();
    //     }
    //   } catch (error) {
    //     console.log("Video autoplay failed:", error);
    //   }
    // };

    // playVideo();

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe all sections with animation classes
    document.querySelectorAll('.animate-on-scroll').forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const handleStartJourney = () => {
    if (isAuthenticated) {
      navigate('/gallery');
    } else {
      setShowLoginModal(true);
    }
  };

  // Gallery modal functions
  const openGalleryModal = (index) => {
    setCurrentGalleryIndex(index);
    setGalleryModalOpen(true);
  };

  const closeGalleryModal = () => {
    setGalleryModalOpen(false);
  };

  const goToPreviousImage = useCallback(() => {
    setCurrentGalleryIndex((prev) => 
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  }, [galleryImages.length]);

  const goToNextImage = useCallback(() => {
    setCurrentGalleryIndex((prev) => 
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  }, [galleryImages.length]);

  // Keyboard navigation for gallery modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryModalOpen) return;
      if (e.key === 'Escape') closeGalleryModal();
      if (e.key === 'ArrowLeft') goToPreviousImage();
      if (e.key === 'ArrowRight') goToNextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryModalOpen, goToPreviousImage, goToNextImage]);

  // Newsletter submit handler
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail || !newsletterPrivacy) {
      setNewsletterMessage({ type: 'error', text: t.newsletterErrorRequired || 'Please fill all required fields' });
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage({ type: '', text: '' });

    try {
      await subscribeNewsletter(newsletterEmail, newsletterRole, newsletterPrivacy);
      setNewsletterMessage({ type: 'success', text: t.newsletterSuccess || 'Successfully subscribed!' });
      setNewsletterEmail('');
      setNewsletterRole('parent');
      setNewsletterPrivacy(false);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t.newsletterErrorGeneric || 'An error occurred. Please try again.' 
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const statistics = [
    {
      number: '10K+',
      label: t.statUsers,
      icon: <svg className="stat-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
      </svg>
    },
    {
      number: '95%',
      label: t.statSuccess,
      icon: <svg className="stat-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
      </svg>
    },
    {
      number: '50+',
      label: t.statSchools,
      icon: <svg className="stat-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z" />
      </svg>
    }
  ];

  const testimonials = [
    {
      text: t.testimonial1Text,
      author: t.testimonial1Author,
      role: t.testimonial1Role,
      icon: '👨‍🎓'
    },
    {
      text: t.testimonial2Text,
      author: t.testimonial2Author,
      role: t.testimonial2Role,
      icon: '👩‍🏫'
    },
    {
      text: t.testimonial3Text,
      author: t.testimonial3Author,
      role: t.testimonial3Role,
      icon: '👨‍👩‍👧‍👦'
    }
  ];

  const faqs = [
    { question: t.faq1Question, answer: t.faq1Answer },
    { question: t.faq2Question, answer: t.faq2Answer },
    { question: t.faq3Question, answer: t.faq3Answer },
    { question: t.faq4Question, answer: t.faq4Answer }
  ];

  return (
    <div className="page-container">
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <main className="home">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <div className="hero-text">
              <h1>{t.welcome}</h1>
              <p className="subtitle">{t.subtitle}</p>
              {isAuthenticated ? (
                <div className="user-info">
                  <span className="welcome-message">
                    {t.welcomeBack} {userData?.first_name || 'User'}
                  </span>
                  {/* <button 
                    className="logout-btn"
                    onClick={() => {
                      localStorage.removeItem('authToken');
                      localStorage.removeItem('userData');
                      setIsAuthenticated(false);
                      setUserData(null);
                    }}
                  >
                    Logout
                  </button> */}
                </div>
              ) : (
                <div className="auth-links">
                  {/* <Link to="/register" className="auth-link register">{t.registerLink}</Link>
                  <Link to="/login" className="auth-link login">{t.loginLink}</Link> */}
                </div>
              )}
            </div>
            <div className="hero-images">
              <div className="rectangle white">
                {showSupportingText && (
                  <div className={`supporting-text ${showSupportingText ? 'fade-in' : ''}`}>
                    <p>{supportingText}<span className="cursor">|</span></p>
                  </div>
                )}
                {showFreeTrial && (
                  <div className={`free-trial-text ${showFreeTrial ? 'fade-in' : ''}`}>
                    <span>{t.freeTrialDays}</span>
                    <span>{t.freeTrialText}</span>
                  </div>
                )}
                {showStoreButtons && (
                  <div className={`store-buttons-container ${showStoreButtons ? 'fade-in' : ''}`}>
                    <div className="arrow-down">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4V20M12 20L5 13M12 20L19 13" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="store-buttons">
                      <a href="https://play.google.com/store/apps/details?id=com.DysOrtoInnovations.DysOrto" className="store-button google-play" target="_blank" rel="noopener noreferrer">
                        <svg viewBox="0 0 24 24" className="store-icon">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                        </svg>
                        <div className="store-text">
                          <span className="store-subtitle">GET IT ON</span>
                          <span className="store-title">Google Play</span>
                        </div>
                      </a>
                      {/* <a href="#" className="store-button app-store">
                        <svg viewBox="0 0 24 24" className="store-icon">
                          <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                        </svg>
                        <div className="store-text">
                          <span className="store-subtitle">Download on the</span>
                          <span className="store-title">App Store</span>
                        </div>
                      </a> */}
                    </div>
                  </div>
                )}
              </div>
              {/* <img src={Kid1} alt="Happy child learning" className="kid kid-1 hide-on-mobile" /> */}
            </div>
          </div>
          <img src={Cloud1} alt="" className="cloud cloud-1" />
          <img src={Cloud2} alt="" className="cloud cloud-2" />
        </section>

        <div className="container">
          {/* Features Section */}
          {/* <section className="features animate-on-scroll">
            <h2>{t.features}</h2>
            <div className="features-grid"> */}
              {/* Features content */}
            {/* </div>
          </section> */}

          {/* Modern Gallery Section */}
          <section className="modern-gallery" ref={el => sectionsRef.current[0] = el}>
            <div className="gallery-header">
              <h2 className="gallery-title">{t.gallery}</h2>
              <p className="gallery-subtitle">Discover the DysOrto experience</p>
            </div>
            <div className="masonry-grid">
              {galleryImages.map((src, index) => (
                <div key={index} className="masonry-item">
                  <div className="gallery-card">
                    <div className="gallery-image">
                      <img src={src} alt={`App Preview ${index + 1}`} />
                    </div>
                    <div className="gallery-overlay" onClick={() => openGalleryModal(index)}>
                      <button className="gallery-button">
                        <span className="zoom-icon">🔍</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="gallery-cta">
              <button className="cta-button" onClick={handleStartJourney}>
                <span>{t.viewMore}</span>
                <svg className="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
                </svg>
              </button>
            </div>
          </section>

          {/* Gallery Modal */}
          {galleryModalOpen && (
            <div className="image-modal-overlay" onClick={closeGalleryModal}>
              <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={closeGalleryModal}>×</button>
                
                <button className="modal-nav modal-prev" onClick={goToPreviousImage}>
                  ‹
                </button>
                
                <div className="modal-image-container">
                  <img src={galleryImages[currentGalleryIndex]} alt={`App Preview ${currentGalleryIndex + 1}`} />
                </div>
                
                <button className="modal-nav modal-next" onClick={goToNextImage}>
                  ›
                </button>
                
                <div className="modal-counter">
                  {currentGalleryIndex + 1} / {galleryImages.length}
                </div>
              </div>
            </div>
          )}

          {/* Testimonials / Reviews Section */}
            {reviews.length > 0 ? (
              <section className="testimonials animate-on-scroll" ref={el => sectionsRef.current[1] = el}>
              <h2>{t.testimonials}</h2>
              <div className="testimonials-scroll-container">
                <div className="testimonials-scroll">
                  {reviews.map((review) => (
                    <div key={review.id} className="testimonial-card">
                      <div className="testimonial-stars">
                        {Array.from({ length: 5 }, (_, i) => (
                          <span key={i} className={`star ${i < review.stars ? 'filled' : ''}`}>★</span>
                        ))}
                      </div>
                      <p className="testimonial-text">{review.comment || ''}</p>
                      <div className="testimonial-author">
                        <strong>{review.full_name}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </section>
            ) : null}
          

          {/* Statistics Section */}
          {/* <section className="statistics animate-on-scroll" ref={el => sectionsRef.current[2] = el}>
            <h2>{t.statistics}</h2>
            <div className="stats-grid">
              {statistics.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon-wrapper">{stat.icon}</div>
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </section> */}

          {/* FAQ Section */}
          <section className="faq animate-on-scroll" ref={el => sectionsRef.current[3] = el}>
            <h2>{t.faqTitle}</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${activeAccordion === index ? 'active' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => setActiveAccordion(activeAccordion === index ? null : index)}
                    aria-expanded={activeAccordion === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className="faq-question-text">{faq.question}</span>
                    <svg className="faq-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                    </svg>
                  </button>
                  <div
                    id={`faq-answer-${index}`}
                    className="faq-answer"
                    role="region"
                    aria-labelledby={`faq-question-${index}`}
                  >
                    {faq.answer}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="contact animate-on-scroll" ref={el => sectionsRef.current[4] = el}>
            <h2>{t.contactTitle}</h2>
            <div className="contact-content">
              <div className="contact-info">
                <h3>{t.contactInfo}</h3>
                <div className="contact-items">
                  <div className="contact-item">
                    <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                    </svg>
                    <a href="mailto:support@dysorto.fr">support@dysorto.fr</a>
                  </div>
                  <div className="contact-item">
                    <svg className="contact-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" />
                    </svg>
                    <span>{t.address}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Newsletter Section */}
          <section className="newsletter animate-on-scroll" ref={el => sectionsRef.current[5] = el}>
            <div className="newsletter-content">
              <div className="newsletter-text">
                <h2>{t.newsletterTitle}</h2>
                <p>{t.newsletterDescription}</p>
              </div>
              <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                <div className="newsletter-input-group">
                  <input 
                    type="email" 
                    placeholder={t.enterEmail}
                    className="newsletter-input"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterLoading}
                    required
                  />
                  <div className="newsletter-type-selection">
                    <label className="newsletter-radio">
                      <input 
                        type="radio" 
                        name="userType" 
                        value="parent"
                        checked={newsletterRole === 'parent'}
                        onChange={(e) => setNewsletterRole(e.target.value)}
                        disabled={newsletterLoading}
                      />
                      <span>{t.iAmParent}</span>
                    </label>
                    <label className="newsletter-radio">
                      <input 
                        type="radio" 
                        name="userType" 
                        value="teacher"
                        checked={newsletterRole === 'teacher'}
                        onChange={(e) => setNewsletterRole(e.target.value)}
                        disabled={newsletterLoading}
                      />
                      <span>{t.iAmTeacher}</span>
                    </label>
                  </div>
                </div>
                <div className="newsletter-actions">
                  <button 
                    type="submit" 
                    className="newsletter-submit"
                    disabled={newsletterLoading}
                  >
                    {newsletterLoading ? (t.loading || 'Loading...') : t.signUp}
                  </button>
                  <label className="newsletter-privacy">
                    <input 
                      type="checkbox"
                      checked={newsletterPrivacy}
                      onChange={(e) => setNewsletterPrivacy(e.target.checked)}
                      disabled={newsletterLoading}
                      required 
                    />
                    <span>
                      <Link to="/privacy" className="privacy-link">
                        {t.privacyConsent}
                      </Link>
                    </span>
                  </label>
                </div>
                {newsletterMessage.text && (
                  <div className={`newsletter-message ${newsletterMessage.type}`}>
                    {newsletterMessage.text}
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* Login Modal */}
          {showLoginModal && (
            <div className="modal-overlay" onClick={() => setShowLoginModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close" onClick={() => setShowLoginModal(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="modal-header">
                  <h2 className="modal-title">{t.readyToStart}</h2>
                  <p className="modal-subtitle">{t.joinUs}</p>
                </div>
                <div className="modal-buttons">
                  <Link to="/login" className="modal-button primary">
                    {t.login}
                  </Link>
                  <Link to="/register" className="modal-button secondary">
                    {t.register}
                  </Link>
                </div>
                <button 
                  className="modal-skip" 
                  onClick={() => setShowLoginModal(false)}
                >
                  {t.notNow}
                </button>
              </div>
            </div>
          )}
        </div>

      </main>
    </div>
  );
}

export default Home;
